import { colors, HighlightIcon, type IconName } from '@dev-spendesk/grapes';

import { SupplierLogo } from 'common/components/SupplierLogo';
import { type TGlobalFunctionTyped } from 'common/hooks/useTranslation';
import { type Payable } from 'modules/payable';
import { type RequestAPI } from 'modules/requests/types';

export type ExpenditureType = RequestAPI['type'] | 'plastic_card';

export const getExpenditureTypeIcon = ({
  type,
  cardId,
  isAtmWithdrawal,
}: {
  type: ExpenditureType | undefined;
  cardId?: string;
  isAtmWithdrawal?: boolean;
  // eslint-disable-next-line sonarjs/cognitive-complexity
}): IconName | undefined => {
  // spendesk billing invoices
  if (!type && !cardId) {
    return 'invoice';
  }

  if (isAtmWithdrawal) {
    return 'banknote';
  }

  if (!type) {
    return;
  }

  if (type === 'mileage_allowance') {
    return 'car';
  }
  if (type === 'per_diem_allowance') {
    return 'globe-eu-africa';
  }
  if (type === 'travel_budget') {
    return 'plane';
  }
  if (type === 'expense') {
    return 'receipt';
  }
  if (type === 'single_purchase') {
    return 'card-lightning';
  }
  if (type === 'subscription') {
    return 'card-rounded-arrows';
  }
  if (type === 'multi_use_card') {
    return 'card-stack';
  }
  if (type === 'invoice') {
    return 'document';
  }
  if (type === 'purchase_order') {
    return 'shopping-cart';
  }
  if (type === 'credit_note') {
    return 'receipt-uturn-left-top';
  }
  if (['card_load', 'subscription_increase'].includes(type)) {
    return 'card-arrow-up';
  }
};

export const getExpenditureType = (
  {
    type,
    cardId,
    isAtmWithdrawal,
  }: {
    type: ExpenditureType | undefined;
    cardId?: string;
    isAtmWithdrawal?: boolean;
  },
  t: TGlobalFunctionTyped,
) => {
  const icon = getExpenditureTypeIcon({
    type,
    isAtmWithdrawal,
    cardId,
  });

  const base: { icon: IconName; color: string } = {
    icon: icon ?? ('card' as IconName),
    color: colors.contentSecondaryBgPrimary,
  };

  switch (type) {
    case 'single_purchase':
      return { ...base, type: t('requests.types.single_purchase') };
    case 'subscription':
      return {
        ...base,
        type: t('requests.types.subscription'),
      };
    case 'subscription_increase':
      return {
        ...base,
        type: t('requests.types.subscription_increase'),
      };
    case 'card_load':
      return { ...base, type: t('requests.types.card_load') };
    case 'multi_use_card': {
      return {
        ...base,
        type: t('requests.types.multi_use_card'),
      };
    }
    case 'expense':
      return {
        ...base,
        type: t('requests.types.expense'),
      };
    case 'invoice':
      return { ...base, type: t('requests.types.invoice') };
    case 'mileage_allowance':
      return {
        ...base,
        type: t('requests.types.mileage_allowance'),
      };
    case 'per_diem_allowance':
      return {
        ...base,
        type: t('requests.types.per_diem_allowance'),
      };
    case 'travel_budget':
      return { ...base, type: t('requests.types.travel_budget') };
    case 'purchase_order':
      return { ...base, type: t('requests.types.purchase_order') };
    case 'credit_note':
      return { ...base, type: t('requests.types.credit_note') };
    case 'plastic_card':
      if (isAtmWithdrawal) {
        return {
          ...base,
          type: t('requests.types.atm_withdrawal'),
        };
      }
      return { ...base, type: t('requests.types.plastic_card') };
    default:
      // spendesk billing invoices
      if (!cardId) {
        return { ...base, type: t('requests.types.invoice') };
      }

      if (isAtmWithdrawal) {
        return {
          ...base,
          type: t('requests.types.atm_withdrawal'),
        };
      }

      return { ...base, type: t('requests.types.plastic_card') };
  }
};

type AvatarSize = 24 | 32 | 40 | 56;

export const getExpenditureAvatar = (
  {
    supplier,
    type,
    size = 32,
    isRequest = false,
  }: {
    supplier?: { id: string | undefined; name: string; url?: string };
    type: ExpenditureType | undefined;
    size?: AvatarSize;
    isRequest?: boolean;
  },
  t: TGlobalFunctionTyped,
) => {
  const icon = getExpenditureTypeIcon({
    type,
    isAtmWithdrawal: undefined,
    cardId: undefined,
  });

  if (type === 'mileage_allowance' && icon) {
    return {
      avatarTitle: t('payables.panel.types.mileage'),
      avatar: (
        <HighlightIcon
          variant="lemon"
          name={icon}
          size={size}
          className="shrink-0"
        />
      ),
    };
  }

  if (type === 'per_diem_allowance' && icon) {
    return {
      avatarTitle: t('payables.panel.types.perDiem'),
      avatar: (
        <HighlightIcon
          variant="lemon"
          name={icon}
          size={size}
          className="shrink-0"
        />
      ),
    };
  }

  if (type === 'travel_budget' && icon) {
    return {
      avatarTitle: t('payables.panel.types.travelBudget'),
      avatar: (
        <HighlightIcon
          variant="purple"
          name={icon}
          size={size}
          className="shrink-0"
        />
      ),
    };
  }

  if (type === 'card_load' && icon) {
    return {
      avatarTitle: t('requests.types.card_load'),
      avatar: (
        <HighlightIcon
          variant="purple"
          name={icon}
          size={size}
          className="shrink-0"
        />
      ),
    };
  }

  if (type === 'multi_use_card' && icon && isRequest) {
    return {
      avatarTitle: t('requests.types.multi_use_card'),
      avatar: (
        <HighlightIcon
          variant="purple"
          name={icon}
          size={size}
          className="shrink-0"
        />
      ),
    };
  }

  return {
    avatarTitle: supplier?.name || t('misc.na'),
    avatar: (
      <SupplierLogo
        name={supplier?.id === 'unknown' ? '' : supplier?.name}
        size={size}
        className="shrink-0"
        lazyLoad
      />
    ),
  };
};

export const payableTypeToExpenditureType = (
  payable: Pick<Payable, 'type' | 'subtype'>,
): ExpenditureType | undefined => {
  if (payable.subtype === 'creditNote') {
    return 'credit_note';
  }
  if (payable.subtype === 'plasticCard') {
    return 'plastic_card';
  }
  if (payable.subtype === 'subscriptionCard') {
    return 'subscription';
  }
  if (payable.subtype === 'singleUseCard') {
    return 'single_purchase';
  }
  if (payable.subtype === 'multiUseCard') {
    return 'multi_use_card';
  }

  if (payable.type === 'per_diem_allowance') {
    return 'per_diem_allowance';
  }

  if (payable.type === 'mileage_allowance') {
    return 'mileage_allowance';
  }

  if (payable.type === 'expense_claim') {
    return 'expense';
  }

  if (payable.type === 'invoice_purchase') {
    return 'invoice';
  }
};
